import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { NotificationSpaceCategoryItem } from "@/features/user-profile/types";
import { notificationApi } from "@/lib/humhub-api/notification";

interface UseNotificationSpacesByCategoryResult {
  dataByCategory: Record<string, NotificationSpaceCategoryItem[]>;
  loadingByCategory: Record<string, boolean>;
  errorByCategory: Record<string, unknown>;
  updateSpaceSetting: (
    spaceId: number,
    category: string,
    enabled: boolean,
  ) => Promise<void>;
  updatingKeys: Set<string>;
}

export const useNotificationSpacesByCategory = (
  categories: string[],
  enabledByCategory: Record<string, boolean>,
): UseNotificationSpacesByCategoryResult => {
  const queryClient = useQueryClient();
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());

  const queries = useQueries({
    queries: categories.map((category) => ({
      queryKey: ["notificationSpacesByCategory", category],
      queryFn: () => notificationApi.getNotificationSpacesByCategory(category),
      enabled: enabledByCategory[category] ?? false,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const dataByCategory = useMemo(() => {
    return categories.reduce(
      (acc, category, index) => {
        acc[category] = queries[index]?.data?.items ?? [];
        return acc;
      },
      {} as Record<string, NotificationSpaceCategoryItem[]>,
    );
  }, [categories, queries]);

  const loadingByCategory = useMemo(() => {
    return categories.reduce(
      (acc, category, index) => {
        acc[category] = Boolean(queries[index]?.isLoading);
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [categories, queries]);

  const errorByCategory = useMemo(() => {
    return categories.reduce(
      (acc, category, index) => {
        acc[category] = queries[index]?.error;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }, [categories, queries]);

  const { mutateAsync } = useMutation({
    mutationFn: (vars: {
      spaceId: number;
      category: string;
      enabled: boolean;
    }) =>
      notificationApi.updateNotificationSpaceCategory(
        vars.spaceId,
        vars.category,
        vars.enabled,
      ),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({
        queryKey: ["notificationSpacesByCategory", vars.category],
      });
      const previous = queryClient.getQueryData<{
        category: string;
        items: NotificationSpaceCategoryItem[];
      }>(["notificationSpacesByCategory", vars.category]);

      if (previous) {
        queryClient.setQueryData(
          ["notificationSpacesByCategory", vars.category],
          {
            ...previous,
            items: previous.items.map((item) =>
              item.space_id === vars.spaceId
                ? { ...item, enabled: vars.enabled }
                : item,
            ),
          },
        );
      }

      return { previous };
    },
    onError: (_error, vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["notificationSpacesByCategory", vars.category],
          context.previous,
        );
      }
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(
        ["notificationSpacesByCategory", vars.category],
        (
          old:
            | {
                category: string;
                items: NotificationSpaceCategoryItem[];
              }
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.space_id === data.space_id ? { ...item, ...data } : item,
            ),
          };
        },
      );
    },
  });

  const updateSpaceSetting = async (
    spaceId: number,
    category: string,
    enabled: boolean,
  ) => {
    const key = `${category}:${spaceId}`;
    setUpdatingKeys((prev) => new Set(prev).add(key));
    try {
      await mutateAsync({ spaceId, category, enabled });
    } finally {
      setUpdatingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return {
    dataByCategory,
    loadingByCategory,
    errorByCategory,
    updateSpaceSetting,
    updatingKeys,
  };
};
